import React from 'react';
import { Card, Typography, Space, Badge, Row, Col } from 'antd';
import {
  Bell,
  Wallet,
  Plus,
  ClipboardList,
  Luggage,
  TrendingUp,
  Inbox,
  Sparkles,
  MapPin,
  Calendar,
  Star,
  ArrowLeft,
  ArrowRight,
  Plane,
  CreditCard,
  Clock,
  Trash2,
  Users,
  Sun,
  Route,
  Share2,
  Check
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../contexts/LocaleContext';
import styles from '../css/HomePage.module.css';

const { Text, Title } = Typography;

interface HomePageProps {
  userName: string;
  walletBalance: number;
  notificationCount: number;
  onNotificationClick: () => void;
  onQuickAction: (action: string) => void;
  userRole?: 'user' | 'agency' | 'admin';
}

const HomePage: React.FC<HomePageProps> = ({
  userName,
  walletBalance,
  notificationCount,
  onNotificationClick,
  onQuickAction,
  userRole = 'user'
}) => {
  const { t } = useTranslation('common');
  const { isRTL, locale } = useLocale();

  const getQuickActions = () => {
    return [
      { key: 'reservation', title: t('home.quickAction.reservation'), icon: <ClipboardList size={20} />, color: '#3b82f6' },
      { key: 'trip', title: t('home.quickAction.trip'), icon: <Luggage size={20} />, color: '#10b981' },
      { key: 'wallet', title: t('home.quickAction.wallet'), icon: <TrendingUp size={20} />, color: '#f59e0b' },
      { key: 'notification', title: t('home.quickAction.notification'), icon: <Bell size={20} />, color: '#ef4444' },
      { key: 'requests', title: t('home.quickAction.requests'), icon: <Inbox size={20} />, color: '#8b5cf6' },
      { key: 'trips', title: t('home.quickAction.trips'), icon: <MapPin size={20} />, color: '#06b6d4' },
    ];
  };

  const quickActions = getQuickActions();
  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        {/* Background Pattern */}
        <div className={styles.headerPattern} />
        
        <div className={styles.headerContent}>
          {/* Logo */}
          <div className={styles.logoContainer}>
            <img 
              src="/logo.png" 
              alt="Logo" 
              className={styles.logo}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            <div className={styles.logoFallback}>
              P
            </div>
          </div>

          {/* Notifications */}
          <div className={styles.notificationBadge}>
            <Badge count={notificationCount} size="small">
              <div
                className={styles.notificationButton}
                onClick={onNotificationClick}
              >
                <Bell size={20} color="white" />
              </div>
            </Badge>
          </div>
        </div>

        {/* Welcome Message */}
        <div className={styles.welcomeSection}>
          <Text className={styles.welcomeText}>
            {t('home.welcome')} {userName} 👋
          </Text>
        </div>

        {/* Wallet Balance */}
        <div className={styles.walletSection}>
          <div className={styles.walletContent}>
            <div className={styles.walletInfo}>
              <Wallet size={18} color="white" />
              <Text className={styles.walletText}>
                {t('wallet.title')}
              </Text>
            </div>
            <Text className={styles.walletAmount}>
              {walletBalance.toLocaleString()} ریال
            </Text>
          </div>
        </div>
      </div>

      {/* Quick Actions Section */}
      <div className={styles.quickActionsContainer}>
        <Row gutter={[12, 12]}>
          {quickActions.map((action, index) => (
            <Col span={8} key={action.key}>
              <div
                className={styles.quickActionButton}
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => onQuickAction(action.key)}
              >
                <div className={styles.quickActionIcon} style={{ backgroundColor: action.color }}>
                  {action.icon}
                </div>
                <Text className={styles.quickActionButtonText}>
                  {action.title}
                </Text>
              </div>
            </Col>
          ))}
        </Row>
      </div>

      {/* Content Sections */}
      <div className={styles.contentContainer}>
        {/* Reminders Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Title level={4} className={styles.sectionTitle}>
              یادآوری ها
            </Title>
            <Text className={styles.viewAllLink}>
              {t('home.viewAll')}
            </Text>
          </div>

          <Space direction={'vertical'} style={{ width: '100%' }}>
            {/* Flight Reminder */}
            <Card
              className={styles.reminderCard}
              styles={{ body: { padding: '16px' } }}
              hoverable
            >
              <div className={styles.reminderContent}>
                <div className={styles.reminderHeader}>
                  <div className={styles.reminderIconWrapper} style={{ backgroundColor: '#ef4444' }}>
                    <Plane size={18} color="white" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <Text className={styles.reminderTitle} style={{ textAlign: isRTL ? 'right' : 'left' }}>
                        یادآوری پرواز
                      </Text>
                      <Text className={styles.reminderTime} style={{ textAlign: isRTL ? 'left' : 'right' }}>
                        2 ساعت پیش
                      </Text>
                    </div>
                    <Text className={styles.reminderDescription} style={{ textAlign: isRTL ? 'right' : 'left' }}>
                      پرواز شما به استانبول فردا ساعت 14:30 است
                    </Text>
                  </div>
                </div>
                <div className={styles.reminderCountdown}>
                  <Clock size={14} color="#ef4444" />
                  <Text className={styles.reminderCountdownText}>
                    02:00:30 باقی‌مانده
                  </Text>
                </div>
                <div className={styles.reminderActions}>
                  <Trash2 size={16} className={styles.reminderDeleteIcon} />
                </div>
              </div>
            </Card>

            {/* Expense Reminder */}
            <Card
              className={styles.reminderCard}
              styles={{ body: { padding: '16px' } }}
              hoverable
            >
              <div className={styles.reminderContent}>
                <div className={styles.reminderHeader}>
                  <div className={styles.reminderIconWrapper} style={{ backgroundColor: '#f59e0b' }}>
                    <CreditCard size={18} color="white" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <Text className={styles.reminderTitle} style={{ textAlign: isRTL ? 'right' : 'left' }}>
                        هزینه جدید
                      </Text>
                      <Text className={styles.reminderTime} style={{ textAlign: isRTL ? 'left' : 'right' }}>
                        1 روز پیش
                      </Text>
                    </div>
                    <Text className={styles.reminderDescription} style={{ textAlign: isRTL ? 'right' : 'left' }}>
                      هزینه هتل توسط سارا اضافه شد
                    </Text>
                  </div>
                </div>
                <div className={styles.reminderActions}>
                  <Trash2 size={16} className={styles.reminderDeleteIcon} />
                </div>
              </div>
            </Card>

            {/* Low Priority Reminder */}
            <Card
              className={styles.reminderCard}
              styles={{ body: { padding: '16px' } }}
              hoverable
            >
              <div className={styles.reminderContent}>
                <div className={styles.reminderHeader}>
                  <div className={styles.reminderIconWrapper} style={{ backgroundColor: '#10b981' }}>
                    <Bell size={18} color="white" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <Text className={styles.reminderTitle} style={{ textAlign: isRTL ? 'right' : 'left' }}>
                        یادآوری سفر
                      </Text>
                      <Text className={styles.reminderTime} style={{ textAlign: isRTL ? 'left' : 'right' }}>
                        3 روز پیش
                      </Text>
                    </div>
                    <Text className={styles.reminderDescription} style={{ textAlign: isRTL ? 'right' : 'left' }}>
                      برای سفر بعدی آماده شوید
                    </Text>
                  </div>
                </div>
                <div className={styles.reminderActions}>
                  <Trash2 size={16} className={styles.reminderDeleteIcon} />
                </div>
              </div>
            </Card>
          </Space>
        </div>

        {/* Suggestions Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Title level={4} className={styles.sectionTitle}>
              {t('home.suggestions')} ✨
            </Title>
            <Text className={styles.viewAllLink}>
              {t('home.viewAll')}
            </Text>
          </div>

          <Row gutter={[12, 12]}>
            {/* Suggestion Card 1 */}
            <Col span={12}>
              <Card
                className={styles.suggestionCard}
                styles={{ body: { padding: '12px' } }}
                hoverable
              >
                <div className={styles.suggestionImage}>
                  <Sparkles size={24} color="#3b82f6" />
                </div>
                <div className={styles.suggestionContent}>
                  <Text className={styles.suggestionTitle}>
                    {locale === 'fa' ? 'سفر به دبی' : 'Dubai Trip'}
                  </Text>
                  <Text className={styles.suggestionSubtitle}>
                    {t('home.startingFrom', { price: locale === 'fa' ? '۵۰۰ دلار' : '$500' })}
                  </Text>
                  <div className={styles.suggestionOrganizer}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      ایجادکننده:
                    </Text>
                    <Text
                      className={styles.organizerLink}
                      onClick={() => onQuickAction('users')}
                    >
                      علی محمدی
                    </Text>
                  </div>
                  <div className={styles.suggestionRating}>
                    <Star size={12} color="#f59e0b" fill="#f59e0b" />
                    <Text className={styles.suggestionRatingText}>4.8</Text>
                  </div>
                </div>
              </Card>
            </Col>

            {/* Suggestion Card 2 */}
            <Col span={12}>
              <Card
                className={styles.suggestionCard}
                styles={{ body: { padding: '12px' } }}
                hoverable
              >
                <div className={styles.suggestionImage}>
                  <MapPin size={24} color="#10b981" />
                </div>
                <div className={styles.suggestionContent}>
                  <Text className={styles.suggestionTitle}>
                    {locale === 'fa' ? 'سفر به ترکیه' : 'Turkey Trip'}
                  </Text>
                  <Text className={styles.suggestionSubtitle}>
                    {t('home.startingFrom', { price: locale === 'fa' ? '۳۰۰ دلار' : '$300' })}
                  </Text>
                  <div className={styles.suggestionOrganizer}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      ایجادکننده:
                    </Text>
                    <Text
                      className={styles.organizerLink}
                      onClick={() => onQuickAction('users')}
                    >
                      سارا احمدی
                    </Text>
                  </div>
                  <div className={styles.suggestionRating}>
                    <Star size={12} color="#f59e0b" fill="#f59e0b" />
                    <Text className={styles.suggestionRatingText}>4.6</Text>
                  </div>
                </div>
              </Card>
            </Col>

            {/* Suggestion Card 3 */}
            <Col span={12}>
              <Card
                className={styles.suggestionCard}
                styles={{ body: { padding: '12px' } }}
                hoverable
              >
                <div className={styles.suggestionImage}>
                  <Calendar size={24} color="#8b5cf6" />
                </div>
                <div className={styles.suggestionContent}>
                  <Text className={styles.suggestionTitle}>
                    {t('home.specialTour')}
                  </Text>
                  <Text className={styles.suggestionSubtitle}>
                    {locale === 'fa' ? '۲۰٪ تخفیف ویژه' : '20% Special Discount'}
                  </Text>
                  <div className={styles.suggestionOrganizer}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      ایجادکننده:
                    </Text>
                    <Text
                      className={styles.organizerLink}
                      onClick={() => onQuickAction('users')}
                    >
                      مریم کریمی
                    </Text>
                  </div>
                  <div className={styles.suggestionBadge}>
                    <Text className={styles.suggestionBadgeText}>{t('home.specialOffer')}</Text>
                  </div>
                </div>
              </Card>
            </Col>

            {/* Suggestion Card 4 */}
            <Col span={12}>
              <Card
                className={styles.suggestionCard}
                styles={{ body: { padding: '12px' } }}
                hoverable
              >
                <div className={styles.suggestionImage}>
                  <Luggage size={24} color="#f59e0b" />
                </div>
                <div className={styles.suggestionContent}>
                  <Text className={styles.suggestionTitle}>
                    {t('home.familyPackage')}
                  </Text>
                  <Text className={styles.suggestionSubtitle}>
                    {t('home.economyPackage')}
                  </Text>
                  <div className={styles.suggestionOrganizer}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      ایجادکننده:
                    </Text>
                    <Text
                      className={styles.organizerLink}
                      onClick={() => onQuickAction('users')}
                    >
                      حسن رضایی
                    </Text>
                  </div>
                  <div className={styles.suggestionRating}>
                    <Star size={12} color="#f59e0b" fill="#f59e0b" />
                    <Text className={styles.suggestionRatingText}>4.9</Text>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </div>

        {/* My Checklists Section */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Title level={4} style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
              چک لیست های من
            </Title>
            <Text style={{ fontSize: '14px', color: '#3b82f6', cursor: 'pointer', fontWeight: '500' }}>
              {t('home.viewAll')}
            </Text>
          </div>

          <Card
            style={{
              borderRadius: '16px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              background: 'white',
              border: 'none'
            }}
            styles={{ body: { padding: '16px' } }}
          >
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <Text style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', textAlign: isRTL ? 'right' : 'left' }}>
                  وسایل سفر استانبول
                </Text>
                <div style={{
                  background: '#3b82f6',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  3/5
                </div>
              </div>

              {/* Animated Progress Bar */}
              <div className={styles.checklistProgressContainer}>
                <div className={styles.checklistProgressHeader}>
                  <Text className={styles.checklistProgressLabel}>
                    پیشرفت کلی
                  </Text>
                  <Text className={styles.checklistProgressValue}>
                    60% تکمیل شده
                  </Text>
                </div>
                <div className={styles.checklistProgressBar}>
                  <div className={styles.checklistProgressFill}></div>
                </div>
              </div>
            </div>

            <Space direction={'vertical'} style={{ width: '100%' }}>
              <div className={styles.checklistItemCompleted}>
                <div className={styles.checklistItemIconCompleted}>
                  <Check size={14} color="white" />
                </div>
                <Text className={styles.checklistItemTextCompleted}>
                  پاسپورت
                </Text>
              </div>

              <div className={styles.checklistItemCompleted}>
                <div className={styles.checklistItemIconCompleted}>
                  <Check size={14} color="white" />
                </div>
                <Text className={styles.checklistItemTextCompleted}>
                  بلیت پرواز
                </Text>
              </div>

              <div className={styles.checklistItemCompleted}>
                <div className={styles.checklistItemIconCompleted}>
                  <Check size={14} color="white" />
                </div>
                <Text className={styles.checklistItemTextCompleted}>
                  رزرو هتل
                </Text>
              </div>

              <div className={styles.checklistItemPending}>
                <div className={styles.checklistItemIconPending}></div>
                <Text className={styles.checklistItemTextPending}>
                  بیمه مسافرتی
                </Text>
              </div>

              <div className={styles.checklistItemPending}>
                <div className={styles.checklistItemIconPending}></div>
                <Text className={styles.checklistItemTextPending}>
                  پول نقد
                </Text>
              </div>
            </Space>

            {/* Suggested Items */}
            <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
              <Text style={{ fontSize: '13px', color: '#64748b', fontWeight: '500', marginBottom: '8px' }}>
                💡 پیشنهاد بر اساس سفر شما: شارژر موبایل، کارت اقامت
              </Text>
            </div>
          </Card>
        </div>

        {/* My Trips Management Section */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Title level={4} style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
              {t('dashboard.title')}
            </Title>
            <Text style={{ fontSize: '14px', color: '#3b82f6', cursor: 'pointer', fontWeight: '500' }}>
              {t('home.viewAll')}
            </Text>
          </div>

          <Card
            style={{
              borderRadius: '16px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              background: 'white',
              border: 'none',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            styles={{ body: { padding: '0' } }}
            hoverable
          >
            <div style={{
              height: '160px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              position: 'relative',
              padding: '20px',
              color: 'white'
            }}>
              {/* Trip Image/Gradient Background */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                background: 'url("data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23ffffff\" fill-opacity=\"0.05\"%3E%3Ccircle cx=\"30\" cy=\"30\" r=\"2\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                opacity: 0.3
              }}></div>

              {/* Trip Header */}
              <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <Text style={{ fontSize: '20px', fontWeight: '700', color: 'white' }}>
                  سفر تهران - استانبول
                </Text>
                <div style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  fontSize: '12px',
                  color: 'white',
                  fontWeight: '700',
                  backdropFilter: 'blur(10px)'
                }}>
                  فعال
                </div>
              </div>

              {/* Trip Date */}
              <Text style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '8px' }}>
                15 اکتبر - 20 اکتبر 2024
              </Text>

              {/* Weather Info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Sun size={16} color="white" />
                <Text style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '500' }}>
                  آب و هوا: 25°C ☀️
                </Text>
              </div>

              {/* Participants */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Users size={14} color="white" />
                </div>
                <Text style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '500' }}>
                  2+4 نفر
                </Text>
              </div>
            </div>

            {/* Bottom Section */}
            <div style={{ padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Route */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MapPin size={14} color="#64748b" />
                    <Text style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                      تهران → استانبول
                    </Text>
                  </div>
                </div>

                {/* Share Button */}
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}>
                  <Share2 size={16} color="#64748b" />
                </div>
              </div>

              {/* Progress */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  flex: 1,
                  height: '8px',
                  background: '#e5e7eb',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div className={styles.tripProgressFill}></div>
                </div>
                <Text style={{ fontSize: '12px', color: '#374151', fontWeight: '600' }}>
                  75% تکمیل شده
                </Text>
              </div>
            </div>
          </Card>
        </div>

        {/* Automatic Reservations Section */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Title level={4} style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
              رزروهای خودکار
            </Title>
            <Text style={{ fontSize: '14px', color: '#3b82f6', cursor: 'pointer', fontWeight: '500' }}>
              {t('home.viewAll')}
            </Text>
          </div>

          <Card
            style={{
              borderRadius: '16px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              background: 'white',
              border: 'none',
              overflow: 'hidden'
            }}
            styles={{ body: { padding: '0' } }}
            hoverable
          >
            <div style={{ padding: '16px' }}>
              {/* Reservation Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <Text style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', textAlign: isRTL ? 'right' : 'left' }}>
                  رزرو پرواز تهران - استانبول
                </Text>
                <div className={styles.reservationStatusPending}>
                  در انتظار
                </div>
              </div>

              {/* Progress Timeline */}
              <div style={{ marginBottom: '16px' }}>
                <Text style={{ fontSize: '13px', color: '#64748b', fontWeight: '500', marginBottom: '8px' }}>
                  وضعیت رزرو (گام به گام)
                </Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* Step 1 - Completed */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: 'white',
                      fontWeight: '700'
                    }}>
                      ✓
                    </div>
                    <Text style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>
                      ایجاد
                    </Text>
                  </div>

                  {/* Line */}
                  <div style={{ flex: 1, height: '2px', background: '#10b981' }}></div>

                  {/* Step 2 - Completed */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: 'white',
                      fontWeight: '700'
                    }}>
                      ✓
                    </div>
                    <Text style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>
                      بررسی
                    </Text>
                  </div>

                  {/* Line */}
                  <div style={{ flex: 1, height: '2px', background: '#10b981' }}></div>

                  {/* Step 3 - In Progress */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <div className={styles.timelineStepInProgress}>
                      ⚙
                    </div>
                    <Text style={{ fontSize: '11px', color: '#3b82f6', fontWeight: '600' }}>
                      در حال پردازش
                    </Text>
                  </div>

                  {/* Line */}
                  <div style={{ flex: 1, height: '2px', background: '#e5e7eb' }}></div>

                  {/* Step 4 - Pending */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: '#9ca3af',
                      fontWeight: '700'
                    }}>
                      4
                    </div>
                    <Text style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '500' }}>
                      تکمیل
                    </Text>
                  </div>
                </div>
              </div>

              {/* Reservation Details */}
              <div style={{
                padding: '12px',
                background: '#f8fafc',
                borderRadius: '12px',
                marginBottom: '12px'
              }}>
                <Space direction={'vertical'} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      background: '#f97316',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Plane size={12} color="white" />
                    </div>
                    <Text style={{ fontSize: '14px', color: '#374151', textAlign: isRTL ? 'right' : 'left' }}>
                      مبدا: تهران (IKA)
                    </Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      background: '#f97316',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Plane size={12} color="white" />
                    </div>
                    <Text style={{ fontSize: '14px', color: '#374151', textAlign: isRTL ? 'right' : 'left' }}>
                      مقصد: استانبول (IST)
                    </Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      background: '#f97316',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Calendar size={12} color="white" />
                    </div>
                    <Text style={{ fontSize: '14px', color: '#374151', textAlign: isRTL ? 'right' : 'left' }}>
                      تاریخ: 15 اکتبر 2024
                    </Text>
                  </div>
                </Space>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#ef4444',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}>
                  کنسل کردن
                </button>
                <button style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#3b82f6',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}>
                  مشاهده جزئیات
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
